import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { motion, AnimatePresence } from 'framer-motion';
import { AppShell3 } from '@/components/app-shell-3';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Globe,
  Phone,
  MapPin,
  Mail,
  Building2,
  Calendar,
  DollarSign,
  Users,
  BookOpen,
  Eye,
  Edit,
  Trash,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText,
  ClipboardCheck,
  GraduationCap,
  ArrowLeft,
  Search,
  Plus,
  Check,
  X,
  Copy,
  MoreVertical,
  CheckCircle,
  AlertCircle,
  Clock,
  Briefcase,
  Star
} from 'lucide-react';
import { PageTransition } from '@/components/PageTransition';
import { LoadingSpinner } from '@/components/LoadingSpinner';

export function TrainerProfilePage() {
  const { trainerId } = useParams();
  const navigate = useNavigate();
  const { company, userProfile } = useAuth();
  const [trainer, setTrainer] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set page title with trainer name when loaded
  usePageTitle(trainer?.name || 'Training Provider');

  const [activeTab, setActiveTab] = useState('quotes');
  const [previousTab, setPreviousTab] = useState('quotes');
  const [courses, setCourses] = useState([]);
  const [editingCourse, setEditingCourse] = useState(null);
  const [newCourse, setNewCourse] = useState(null);
  const [isAddingCourse, setIsAddingCourse] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    itemsPerPage: 25,
    totalItems: 0,
    totalPages: 0
  });
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [courseSearch, setCourseSearch] = useState('');
  const searchDebounceRef = useRef(null);

  // Real data states
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [completedTraining, setCompletedTraining] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const [userSpaces, setUserSpaces] = useState([]);

  // BOHSA trainer ID constant
  const BOHSA_TRAINER_ID = '04045648-327f-4f03-9054-5424e6e2bccd';

  // Tab order for determining slide direction
  const tabOrder = ['quotes', 'bookings', 'courses'];
  const getTabIndex = (tab) => tabOrder.indexOf(tab);

  useEffect(() => {
    if (trainerId && company?.id) {
      fetchTrainer();
      fetchCourses();
      fetchQuotes();
      fetchBookings();
      fetchCompletedTraining();
    }
  }, [trainerId, company?.id]);

  useEffect(() => {
    if (userProfile?.id && company?.id) {
      fetchUserSpaces();
    }
  }, [userProfile?.id, company?.id]);

  useEffect(() => {
    if (!trainerId || !company?.id) return;

    // Set up real-time subscription for courses
    const subscription = supabase
      .channel(`courses-${trainerId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'courses',
          filter: `trainer_id=eq.${trainerId}`
        },
        () => {
          // Refetch courses when changes occur
          fetchCourses();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [trainerId, company?.id]);

  const fetchTrainer = async () => {
    setLoading(true);
    try {
      // Fetch trainer - allow viewing both global trainers (company_id IS NULL) and company trainers
      const { data, error } = await supabase
        .from('trainers')
        .select('*')
        .eq('id', trainerId)
        .or(`company_id.is.null,company_id.eq.${company.id}`)
        .single();

      if (error) throw error;

      setTrainer(data);
    } catch (error) {
      console.error('Error fetching trainer:', error);
      toast.error('Failed to load trainer details');
      navigate('/trainers');
    } finally {
      setLoading(false);
    }
  };

  // Fetch active quotes for this trainer
  const fetchQuotes = async () => {
    setLoadingQuotes(true);
    try {
      // Get quote options for this trainer where the parent quote is in 'live' status
      const { data, error } = await supabase
        .from('quote_options')
        .select(`
          id,
          course_name,
          course_delivery,
          quote_value,
          total,
          obfuscated_location,
          onsite_postcode,
          dates,
          pricing_model,
          training_quote:training_quotes!training_quote_id (
            id,
            status,
            delegates,
            locations,
            company_id,
            created_date
          )
        `)
        .eq('training_provider_id', trainerId)
        .order('created_date', { ascending: false });

      if (error) throw error;

      // Filter to only show quotes where status is 'live' AND belongs to user's company
      const activeQuotes = (data || [])
        .filter(q => q.training_quote?.status === 'live' && q.training_quote?.company_id === company.id)
        .map(q => ({
          id: q.id,
          course: q.course_name,
          delivery: q.course_delivery || 'N/A',
          location: q.obfuscated_location || q.onsite_postcode || q.training_quote?.locations || 'N/A',
          dates: formatQuoteDates(q.dates),
          price: q.total ? `£${parseFloat(q.total).toFixed(2)}` : (q.quote_value ? `£${parseFloat(q.quote_value).toFixed(2)}` : 'TBC'),
          type: q.pricing_model || 'Per Course',
          delegates: q.training_quote?.delegates || '-',
          quoteId: q.training_quote?.id
        }));

      setQuotes(activeQuotes);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Failed to load quotes');
    } finally {
      setLoadingQuotes(false);
    }
  };

  // Fetch bookings for this trainer (quotes that have been booked)
  const fetchBookings = async () => {
    setLoadingBookings(true);
    try {
      const { data, error } = await supabase
        .from('quote_options')
        .select(`
          id,
          course_name,
          course_delivery,
          quote_value,
          total,
          obfuscated_location,
          onsite_postcode,
          dates,
          pricing_model,
          booking_id,
          training_quote:training_quotes!training_quote_id (
            id,
            status,
            delegates,
            locations,
            company_id,
            booked_date,
            booked_quote_id,
            booking_confirmed_date,
            created_date
          )
        `)
        .eq('training_provider_id', trainerId)
        .order('created_date', { ascending: false });

      if (error) throw error;

      // Filter to show booked quotes where this trainer's option was selected AND belongs to user's company
      const bookedQuotes = (data || [])
        .filter(q => {
          // Must belong to user's company
          if (q.training_quote?.company_id !== company.id) return false;
          // Check if the parent quote is booked AND this option was the one booked
          if (q.training_quote?.status !== 'booked') return false;
          // Check if this specific option was booked (by booking_id or by matching booked_quote_id)
          if (q.booking_id) return true;
          if (q.training_quote?.booked_quote_id === q.id) return true;
          return false;
        })
        .map(q => ({
          id: q.id,
          course: q.course_name,
          delivery: q.course_delivery || 'N/A',
          location: q.obfuscated_location || q.onsite_postcode || q.training_quote?.locations || 'N/A',
          dates: q.training_quote?.booked_date
            ? new Date(q.training_quote.booked_date).toLocaleDateString('en-GB')
            : formatQuoteDates(q.dates),
          price: q.total ? `£${parseFloat(q.total).toFixed(2)}` : (q.quote_value ? `£${parseFloat(q.quote_value).toFixed(2)}` : 'TBC'),
          type: q.pricing_model || 'Per Course',
          delegates: q.training_quote?.delegates || '-',
          quoteId: q.training_quote?.id,
          bookedDate: q.training_quote?.booking_confirmed_date
        }));

      setBookings(bookedQuotes);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoadingBookings(false);
    }
  };

  // Fetch completed training for this trainer
  const fetchCompletedTraining = async () => {
    setLoadingCompleted(true);
    try {
      const { data, error } = await supabase
        .from('quote_options')
        .select(`
          id,
          course_name,
          course_delivery,
          quote_value,
          total,
          obfuscated_location,
          onsite_postcode,
          dates,
          pricing_model,
          training_quote:training_quotes!training_quote_id (
            id,
            status,
            delegates,
            locations,
            company_id,
            booked_date,
            completed_date,
            created_date
          )
        `)
        .eq('training_provider_id', trainerId)
        .order('created_date', { ascending: false });

      if (error) throw error;

      // Filter to show completed training (where completed_date is set) AND belongs to user's company
      const completed = (data || [])
        .filter(q => q.training_quote?.completed_date && q.training_quote?.company_id === company.id)
        .map(q => ({
          id: q.id,
          course: q.course_name,
          delivery: q.course_delivery || 'N/A',
          location: q.obfuscated_location || q.onsite_postcode || q.training_quote?.locations || 'N/A',
          dates: q.training_quote?.booked_date
            ? new Date(q.training_quote.booked_date).toLocaleDateString('en-GB')
            : formatQuoteDates(q.dates),
          price: q.total ? `£${parseFloat(q.total).toFixed(2)}` : (q.quote_value ? `£${parseFloat(q.quote_value).toFixed(2)}` : 'TBC'),
          delegates: q.training_quote?.delegates || '-',
          passRate: '-', // Would need to be tracked in booking_delegates
          completedDate: q.training_quote?.completed_date
        }));

      setCompletedTraining(completed);
    } catch (error) {
      console.error('Error fetching completed training:', error);
      toast.error('Failed to load completed training');
    } finally {
      setLoadingCompleted(false);
    }
  };

  // Helper to format dates from quote options
  // Fetch user's spaces for quote builder navigation
  const fetchUserSpaces = async () => {
    try {
      // Fetch all projects for the user's company
      const { data, error } = await supabase
        .from('projects')
        .select('id, name')
        .eq('company_id', company?.id)
        .eq('is_archived', false)
        .order('name', { ascending: true });

      if (error) throw error;

      setUserSpaces(data || []);
    } catch (error) {
      console.error('Error fetching user spaces:', error);
    }
  };

  const formatQuoteDates = (dates) => {
    if (!dates || !Array.isArray(dates) || dates.length === 0) return 'TBC';

    const firstDate = dates[0];
    if (firstDate.startDate) {
      const start = new Date(firstDate.startDate).toLocaleDateString('en-GB');
      if (firstDate.endDate && firstDate.endDate !== firstDate.startDate) {
        const end = new Date(firstDate.endDate).toLocaleDateString('en-GB');
        return `${start} - ${end}`;
      }
      return start;
    }
    return 'TBC';
  };

  const fetchCRMCourses = async (page = 1, search = '') => {
    setLoadingCourses(true);
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await fetch(
        `https://leqtvmuqmyxkdkaxxfqu.supabase.co/functions/v1/api/courses?page=${page}&limit=${pagination.itemsPerPage}${searchParam}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            apikey: 'bohsa-api-xDZ8kuCTQz3axI8VhhjJOQoW3YMB0jcX'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch CRM courses');
      }

      const result = await response.json();

      // Transform CRM courses to match our component format
      const transformedCourses = result.data.map(course => ({
        id: course.id,
        name: course.name,
        // Category data from foreign relations
        category: course.category_data?.type || 'N/A',
        courseCategory: course.course_category_data?.type || 'N/A',
        // Additional fields for popover
        description: course.description || course.objectives || course.content || '',
        suitable_for: course.suitable_for,
        requirements: course.requirements,
        objectives: course.objectives
      }));

      setCourses(transformedCourses);

      // Update pagination state
      if (result.search?.pagination) {
        setPagination({
          page: result.search.pagination.page,
          itemsPerPage: result.search.pagination.limit,
          totalItems: result.search.pagination.total_items,
          totalPages: result.search.pagination.total_pages
        });
      }
    } catch (error) {
      console.error('Error fetching CRM courses:', error);
      // Don't show error toast for CRM failures - just show empty state
      setCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchCourses = async () => {
    // Check if this is the BOHSA trainer - if so, fetch from CRM
    if (trainerId === BOHSA_TRAINER_ID) {
      await fetchCRMCourses(pagination.page, courseSearch);
      return;
    }

    // Otherwise, fetch from local database
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('trainer_id', trainerId)
        .eq('company_id', company.id)
        .order('display_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;

      // Transform the data to match our component format
      const transformedCourses = data.map(course => ({
        ...course,
        updatedDate: new Date(course.updated_date).toLocaleDateString('en-GB')
      }));

      setCourses(transformedCourses);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    }
  };

  // Pagination handlers for CRM courses
  const handlePageChange = async (newPage) => {
    if (trainerId === BOHSA_TRAINER_ID) {
      await fetchCRMCourses(newPage, courseSearch);
    }
  };

  // Search handler with debounce
  const handleCourseSearch = (value) => {
    setCourseSearch(value);

    // Clear existing timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Debounce the API call
    searchDebounceRef.current = setTimeout(() => {
      if (trainerId === BOHSA_TRAINER_ID) {
        fetchCRMCourses(1, value);
      }
    }, 300);
  };

  const handlePreviousPage = () => {
    if (pagination.page > 1) {
      handlePageChange(pagination.page - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.totalPages) {
      handlePageChange(pagination.page + 1);
    }
  };

  // Course management functions
  const handleAddCourse = () => {
    const newCourseData = {
      id: `new-${Date.now()}`,
      name: '',
      accreditation: '',
      updatedDate: new Date().toLocaleDateString('en-GB'),
      isNew: true
    };
    setNewCourse(newCourseData);
    setIsAddingCourse(true);
  };

  const handleSaveCourse = async (course) => {
    if (course.isNew) {
      // Validate new course
      if (!course.name || !course.accreditation) {
        toast.error('Please fill in all required fields');
        return;
      }

      try {
        // Get the max display order
        const maxOrder = Math.max(...courses.map(c => c.display_order || 0), 0);

        // Add to database
        const { data, error } = await supabase
          .from('courses')
          .insert({
            trainer_id: trainerId,
            company_id: company.id,
            name: course.name,
            accreditation: course.accreditation,
            display_order: maxOrder + 1
          })
          .select()
          .single();

        if (error) throw error;

        setNewCourse(null);
        setIsAddingCourse(false);
        toast.success('Course added successfully');

        // Refetch to get updated list
        await fetchCourses();
      } catch (error) {
        console.error('Error adding course:', error);
        toast.error('Failed to add course');
      }
    } else {
      // Update existing course
      try {
        const { error } = await supabase
          .from('courses')
          .update({
            name: course.name,
            accreditation: course.accreditation,
            updated_date: new Date().toISOString()
          })
          .eq('id', course.id);

        if (error) throw error;

        setEditingCourse(null);
        toast.success('Course updated successfully');

        // Refetch to get updated list
        await fetchCourses();
      } catch (error) {
        console.error('Error updating course:', error);
        toast.error('Failed to update course');
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingCourse(null);
    setNewCourse(null);
    setIsAddingCourse(false);
  };

  // Navigate to trainers page to edit (edit dialog is there)
  const handleEdit = () => {
    navigate('/trainers');
    toast.info('Use the edit option from the trainers list');
  };

  const handleDeleteCourse = async (courseId) => {
    try {
      const { error } = await supabase
        .from('courses')
        .delete()
        .eq('id', courseId);

      if (error) throw error;

      toast.success('Course deleted');
      // Refetch to get updated list
      await fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course');
    }
  };

  const handleDuplicateCourse = async (course) => {
    try {
      // Get the max display order
      const maxOrder = Math.max(...courses.map(c => c.display_order || 0), 0);

      const { data, error } = await supabase
        .from('courses')
        .insert({
          trainer_id: trainerId,
          company_id: company.id,
          name: `${course.name} (Copy)`,
          accreditation: course.accreditation,
          display_order: maxOrder + 1
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Course duplicated');
      // Refetch to get updated list
      await fetchCourses();
    } catch (error) {
      console.error('Error duplicating course:', error);
      toast.error('Failed to duplicate course');
    }
  };

  const updateNewCourse = (field, value) => {
    setNewCourse(prev => ({ ...prev, [field]: value }));
  };

  const updateEditingCourse = (field, value) => {
    setEditingCourse(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <AppShell3 backgroundColor="#f8f7f3">
        <PageTransition>
          <div className="flex items-center justify-center h-96">
            <LoadingSpinner />
          </div>
        </PageTransition>
      </AppShell3>
    );
  }

  if (!trainer) {
    return (
      <AppShell3 backgroundColor="#f8f7f3">
        <PageTransition>
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <p className="text-muted-foreground mb-4">Trainer not found</p>
            <Button onClick={() => navigate('/trainers')}>
              Back to Trainers
            </Button>
          </div>
        </PageTransition>
      </AppShell3>
    );
  }

  const getInitials = (name) => {
    if (!name) return 'TP';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Calculate trainer stats
  const trainerStats = {
    totalCourses: courses.length,
    activeQuotes: quotes.length,
    activeBookings: bookings.length,
    completedTraining: completedTraining.length
  };

  const ActionButton = ({ children, onClick }) => (
    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onClick}>
      {children}
    </Button>
  );

  return (
    <AppShell3 backgroundColor="#f8f7f3">
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <PageTransition>
            {/* Back Button */}
            <div className="pt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/trainers')}
                className="mb-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Trainers
              </Button>
            </div>

            {/* Profile Header */}
            <div className="pt-8">
              <motion.div
                className="bg-white rounded-lg shadow-lg border border-gray-200"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="p-6">
                  <div className="flex items-start gap-6">
                    {/* Logo/Avatar */}
                    <Avatar className="h-20 w-20 border-2 border-gray-200 flex-shrink-0">
                      <AvatarImage src={trainer.logo_url} alt={trainer.trainer_name} />
                      <AvatarFallback className="text-lg font-semibold">
                        {getInitials(trainer.trainer_name)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      {/* Name and Status Badges */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h1 className="text-2xl font-bold text-gray-900">
                            {trainer.trainer_name}
                          </h1>

                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            {/* Type Badge */}
                            <Badge variant="outline" className="bg-white">
                              <Briefcase className="h-3 w-3 mr-1" />
                              Training Provider
                            </Badge>

                            {/* Verified Badge */}
                            {!trainer.company_id && (
                              <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Verified Provider
                              </Badge>
                            )}

                            {/* Custom/Internal Badge */}
                            {trainer.company_id && (
                              <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                                <Building2 className="h-3 w-3 mr-1" />
                                Custom Provider
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 ml-4">
                          {trainer.website && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(trainer.website, '_blank')}
                            >
                              <Globe className="h-4 w-4 mr-1" />
                              Website
                            </Button>
                          )}
                          {trainer.contact_number && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(`tel:${trainer.contact_number}`, '_self')}
                            >
                              <Phone className="h-4 w-4 mr-1" />
                              Call
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Meta Information Icons Row */}
                      <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-600">
                        {trainer.coverage && (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            <span>{trainer.coverage}</span>
                          </div>
                        )}
                        {trainer.contact_number && (
                          <div className="flex items-center gap-1.5">
                            <Phone className="h-4 w-4 text-gray-400" />
                            <span>{trainer.contact_number}</span>
                          </div>
                        )}
                        {trainer.website && (
                          <div className="flex items-center gap-1.5">
                            <Globe className="h-4 w-4 text-gray-400" />
                            <a
                              href={trainer.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              {trainer.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                            </a>
                          </div>
                        )}
                        {trainer.email && (
                          <div className="flex items-center gap-1.5">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <span>{trainer.email}</span>
                          </div>
                        )}
                      </div>

                      {/* Stats Mini Cards */}
                      <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t">
                        <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 rounded-md border border-gray-200">
                          <GraduationCap className="h-3.5 w-3.5 text-gray-500" />
                          <span className="text-xs font-medium text-gray-600">Courses:</span>
                          <span className="text-xs font-bold text-gray-900">{trainerStats.totalCourses}</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-purple-50 rounded-md border border-purple-200">
                          <Clock className="h-3.5 w-3.5 text-purple-600" />
                          <span className="text-xs font-medium text-purple-700">Active Quotes:</span>
                          <span className="text-xs font-bold text-purple-700">{trainerStats.activeQuotes}</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 rounded-md border border-blue-200">
                          <Calendar className="h-3.5 w-3.5 text-blue-600" />
                          <span className="text-xs font-medium text-blue-700">Bookings:</span>
                          <span className="text-xs font-bold text-blue-700">{trainerStats.activeBookings}</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-green-50 rounded-md border border-green-200">
                          <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                          <span className="text-xs font-medium text-green-700">Completed:</span>
                          <span className="text-xs font-bold text-green-700">{trainerStats.completedTraining}</span>
                        </div>
                      </div>
                    </div>

                    {/* Dropdown Menu - Right Side */}
                    {trainer.company_id && (
                      <div className="flex-shrink-0">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-5 w-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleEdit}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Trainer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Tabs Section */}
            <div className="py-8">
              <Tabs value={activeTab} onValueChange={(value) => {
                setPreviousTab(activeTab);
                setActiveTab(value);
              }} className="w-full">
                {/* Folder tabs */}
                <div>
                  {/* Tabs row with bottom separator line */}
                  <div className="relative flex items-stretch" style={{ marginBottom: '-1px' }}>
                    {[
                      { value: 'quotes', label: 'Quotes', icon: FileText },
                      { value: 'bookings', label: 'Bookings', icon: Calendar },
                      { value: 'courses', label: 'Courses', icon: GraduationCap },
                    ].map((tab) => {
                      const Icon = tab.icon;
                      const isActive = activeTab === tab.value;
                      return (
                        <button
                          key={tab.value}
                          onClick={() => {
                            setPreviousTab(activeTab);
                            setActiveTab(tab.value);
                          }}
                          className={`
                            flex items-center gap-1.5 text-[13px] cursor-pointer transition-colors
                            ${isActive
                              ? 'font-semibold text-gray-900'
                              : 'font-medium text-gray-500 hover:text-gray-700'
                            }
                          `}
                          style={isActive ? {
                            background: 'white',
                            borderTop: '1px solid #e5e7eb',
                            borderLeft: '1px solid #e5e7eb',
                            borderRight: '1px solid #e5e7eb',
                            borderBottom: '1px solid white',
                            borderRadius: '8px 8px 0 0',
                            padding: '10px 20px',
                            position: 'relative',
                            zIndex: 2,
                          } : {
                            background: 'transparent',
                            border: '1px solid transparent',
                            borderBottom: 'none',
                            padding: '10px 20px',
                            position: 'relative',
                            zIndex: 0,
                          }}
                        >
                          <Icon className="h-3.5 w-3.5" />
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Content - the folder body */}
                  <div className="bg-white border border-gray-200 rounded-b-lg p-6" style={{ borderTopRightRadius: '8px' }}>
              <AnimatePresence mode="wait" custom={getTabIndex(activeTab) - getTabIndex(previousTab)}>
                {activeTab === 'quotes' && (
                  <motion.div
                    key="quotes"
                    custom={getTabIndex(activeTab) - getTabIndex(previousTab)}
                    initial={{
                      opacity: 0,
                      x: (getTabIndex(activeTab) - getTabIndex(previousTab)) > 0 ? 300 : -300
                    }}
                    animate={{
                      opacity: 1,
                      x: 0
                    }}
                    exit={{
                      opacity: 0,
                      x: (getTabIndex(activeTab) - getTabIndex(previousTab)) > 0 ? -300 : 300
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30
                    }}
                  >
                    {loadingQuotes ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : quotes.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
                        <h4 className="text-lg font-medium text-muted-foreground">No Active Quotes</h4>
                        <p className="text-sm text-muted-foreground/70 mt-1">
                          When quotes are requested from this trainer, they'll appear here.
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Filter/Search */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="relative w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              placeholder="Search quotes..."
                              className="pl-9 h-9"
                            />
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {quotes.length} quote{quotes.length !== 1 ? 's' : ''}
                          </div>
                        </div>

                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Course</TableHead>
                                <TableHead>Delivery</TableHead>
                                <TableHead>Location</TableHead>
                                <TableHead>Dates</TableHead>
                                <TableHead>Price</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead className="text-center">Delegates</TableHead>
                                <TableHead className="w-[50px]"></TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {quotes.map((quote) => (
                                <TableRow key={quote.id}>
                                  <TableCell className="font-medium">{quote.course}</TableCell>
                                  <TableCell>{quote.delivery}</TableCell>
                                  <TableCell className="max-w-[200px] truncate">{quote.location}</TableCell>
                                  <TableCell>{quote.dates}</TableCell>
                                  <TableCell className="font-semibold">{quote.price}</TableCell>
                                  <TableCell>{quote.type}</TableCell>
                                  <TableCell className="text-center">{quote.delegates}</TableCell>
                                  <TableCell>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                          <MoreVertical className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem>
                                          <Eye className="h-4 w-4 mr-2" />
                                          View Details
                                        </DropdownMenuItem>
                                        <DropdownMenuItem>
                                          <Calendar className="h-4 w-4 mr-2" />
                                          Book Training
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem className="text-red-600">
                                          <Trash className="h-4 w-4 mr-2" />
                                          Decline Quote
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </>
                    )}
                  </motion.div>
                )}

              {activeTab === 'bookings' && (
                <motion.div
                  key="bookings"
                  custom={getTabIndex(activeTab) - getTabIndex(previousTab)}
                  initial={{
                    opacity: 0,
                    x: (getTabIndex(activeTab) - getTabIndex(previousTab)) > 0 ? 300 : -300
                  }}
                  animate={{
                    opacity: 1,
                    x: 0
                  }}
                  exit={{
                    opacity: 0,
                    x: (getTabIndex(activeTab) - getTabIndex(previousTab)) > 0 ? -300 : 300
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30
                  }}
                >
                  {loadingBookings ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : bookings.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Calendar className="h-12 w-12 text-muted-foreground/50 mb-4" />
                      <h4 className="text-lg font-medium text-muted-foreground">No Active Bookings</h4>
                      <p className="text-sm text-muted-foreground/70 mt-1">
                        Confirmed bookings with this trainer will appear here.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Filter/Search */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="relative w-64">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            placeholder="Search bookings..."
                            className="pl-9 h-9"
                          />
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {bookings.length} booking{bookings.length !== 1 ? 's' : ''}
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Course</TableHead>
                              <TableHead>Delivery</TableHead>
                              <TableHead>Location</TableHead>
                              <TableHead>Dates</TableHead>
                              <TableHead>Price</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead className="text-center">Delegates</TableHead>
                              <TableHead className="w-[50px]"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {bookings.map((booking) => (
                              <TableRow key={booking.id}>
                                <TableCell className="font-medium">{booking.course}</TableCell>
                                <TableCell>{booking.delivery}</TableCell>
                                <TableCell className="max-w-[200px] truncate">{booking.location}</TableCell>
                                <TableCell>{booking.dates}</TableCell>
                                <TableCell className="font-semibold">{booking.price}</TableCell>
                                <TableCell>{booking.type}</TableCell>
                                <TableCell className="text-center">{booking.delegates}</TableCell>
                                <TableCell>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8">
                                        <MoreVertical className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem>
                                        <Eye className="h-4 w-4 mr-2" />
                                        View Details
                                      </DropdownMenuItem>
                                      <DropdownMenuItem>
                                        <Edit className="h-4 w-4 mr-2" />
                                        Edit Booking
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem className="text-red-600">
                                        <Trash className="h-4 w-4 mr-2" />
                                        Cancel Booking
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </>
                  )}
                </motion.div>
              )}

              {activeTab === 'courses' && (
                <motion.div
                  key="courses"
                  custom={getTabIndex(activeTab) - getTabIndex(previousTab)}
                  initial={{
                    opacity: 0,
                    x: (getTabIndex(activeTab) - getTabIndex(previousTab)) > 0 ? 300 : -300
                  }}
                  animate={{
                    opacity: 1,
                    x: 0
                  }}
                  exit={{
                    opacity: 0,
                    x: (getTabIndex(activeTab) - getTabIndex(previousTab)) > 0 ? -300 : 300
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30
                  }}
                >
                  <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                  <h3 className="text-lg font-semibold">Courses</h3>
                  {trainerId === BOHSA_TRAINER_ID && (
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search courses..."
                        value={courseSearch}
                        onChange={(e) => handleCourseSearch(e.target.value)}
                        className="pl-9 h-9"
                      />
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="w-full">
                    <table className="w-full table-fixed">
                      <thead>
                        <tr className="border-b">
                          {trainerId === BOHSA_TRAINER_ID ? (
                            <>
                              <th className="text-left py-3 px-3 font-medium text-sm">Course Name</th>
                              <th className="text-left py-3 px-3 font-medium text-sm w-48">Category</th>
                              <th className="text-left py-3 px-3 font-medium text-sm w-48">Course Category</th>
                              <th className="text-right py-3 px-3 font-medium text-sm w-32">Actions</th>
                            </>
                          ) : (
                            <>
                              <th className="text-left py-3 px-3 font-medium text-sm w-24">ID</th>
                              <th className="text-left py-3 px-3 font-medium text-sm">Course Name</th>
                              <th className="text-left py-3 px-3 font-medium text-sm w-40">Accreditation</th>
                              <th className="text-left py-3 px-3 font-medium text-sm w-32">Updated Date</th>
                              <th className="text-right py-3 px-3 font-medium text-sm w-48">Actions</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {loadingCourses ? (
                          <tr>
                            <td colSpan={trainerId === BOHSA_TRAINER_ID ? "4" : "5"} className="py-8 text-center">
                              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
                            </td>
                          </tr>
                        ) : (
                          <AnimatePresence mode="popLayout">
                            {courses.map((course) => (
                            <motion.tr
                              key={course.id}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              layout
                              className="border-b h-[52px]"
                            >
                              {editingCourse?.id === course.id ? (
                                <>
                                  <td className="py-2 px-3">
                                    <span className="font-mono text-sm">{course.id ? String(course.id).slice(0, 6) : ''}</span>
                                  </td>
                                  <td className="py-2 px-3">
                                    <Input
                                      value={editingCourse.name}
                                      onChange={(e) => updateEditingCourse('name', e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleSaveCourse(editingCourse);
                                        } else if (e.key === 'Escape') {
                                          e.preventDefault();
                                          handleCancelEdit();
                                        }
                                      }}
                                      className="h-8"
                                      placeholder="Course name"
                                      autoFocus
                                    />
                                  </td>
                                  <td className="py-2 px-3">
                                    <Input
                                      value={editingCourse.accreditation}
                                      onChange={(e) => updateEditingCourse('accreditation', e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleSaveCourse(editingCourse);
                                        } else if (e.key === 'Escape') {
                                          e.preventDefault();
                                          handleCancelEdit();
                                        }
                                      }}
                                      className="h-8"
                                      placeholder="Accreditation"
                                    />
                                  </td>
                                  <td className="py-2 px-3">
                                    <span className="text-sm">{course.updatedDate}</span>
                                  </td>
                                  <td className="py-2 px-3">
                                    <div className="flex justify-end gap-1">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        onClick={() => handleSaveCourse(editingCourse)}
                                      >
                                        <Check className="h-4 w-4 text-green-600" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        onClick={handleCancelEdit}
                                      >
                                        <X className="h-4 w-4 text-red-600" />
                                      </Button>
                                    </div>
                                  </td>
                                </>
                              ) : (
                                <>
                                  {trainerId === BOHSA_TRAINER_ID ? (
                                    <>
                                      {/* BOHSA trainer columns */}
                                      <td className="py-2 px-3">
                                        {course.description || course.suitable_for || course.requirements || course.objectives ? (
                                          <Popover>
                                            <PopoverTrigger asChild>
                                              <button className="text-sm text-left hover:text-primary hover:underline cursor-pointer transition-colors">
                                                {course.name}
                                              </button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-[500px] max-h-[400px] overflow-y-auto" align="start">
                                              <div className="space-y-4">
                                                <div>
                                                  <h4 className="font-semibold text-lg">{course.name}</h4>
                                                </div>

                                                {course.description && (
                                                  <div>
                                                    <h5 className="text-sm font-semibold text-foreground mb-2">Description</h5>
                                                    <p className="text-sm text-muted-foreground leading-relaxed">{course.description}</p>
                                                  </div>
                                                )}

                                                {course.suitable_for && (
                                                  <div>
                                                    <h5 className="text-sm font-semibold text-foreground mb-2">Suitable For</h5>
                                                    <p className="text-sm text-muted-foreground leading-relaxed">{course.suitable_for}</p>
                                                  </div>
                                                )}

                                                {course.requirements && (
                                                  <div>
                                                    <h5 className="text-sm font-semibold text-foreground mb-2">Requirements</h5>
                                                    <p className="text-sm text-muted-foreground leading-relaxed">{course.requirements}</p>
                                                  </div>
                                                )}

                                                {course.objectives && (
                                                  <div>
                                                    <h5 className="text-sm font-semibold text-foreground mb-2">Objectives</h5>
                                                    {Array.isArray(course.objectives) ? (
                                                      <ul className="space-y-1.5 ml-1">
                                                        {course.objectives.map((objective, idx) => (
                                                          <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                                                            <span className="text-primary mt-1.5">•</span>
                                                            <span className="flex-1 leading-relaxed">{objective}</span>
                                                          </li>
                                                        ))}
                                                      </ul>
                                                    ) : (
                                                      <p className="text-sm text-muted-foreground leading-relaxed">{course.objectives}</p>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            </PopoverContent>
                                          </Popover>
                                        ) : (
                                          <span className="text-sm">{course.name}</span>
                                        )}
                                      </td>
                                      <td className="py-2 px-3">
                                        <span className="text-sm">{course.category}</span>
                                      </td>
                                      <td className="py-2 px-3">
                                        <span className="text-sm">{course.courseCategory}</span>
                                      </td>
                                    </>
                                  ) : (
                                    <>
                                      {/* Regular trainer columns */}
                                      <td className="py-2 px-3">
                                        <span className="font-mono text-sm">{course.id ? String(course.id).slice(0, 6) : ''}</span>
                                      </td>
                                      <td className="py-2 px-3">
                                        <span className="text-sm">{course.name}</span>
                                      </td>
                                      <td className="py-2 px-3">
                                        <span className="text-sm">{course.accreditation}</span>
                                      </td>
                                      <td className="py-2 px-3">
                                        <span className="text-sm">{course.updatedDate}</span>
                                      </td>
                                    </>
                                  )}
                                  <td className="py-2 px-3">
                                    <div className="flex justify-end gap-1">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-xs"
                                          >
                                            Build Quote
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-56">
                                          <DropdownMenuLabel>Select a Space</DropdownMenuLabel>
                                          <DropdownMenuSeparator />
                                          {userSpaces.length === 0 ? (
                                            <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                                              No spaces available
                                            </div>
                                          ) : (
                                            userSpaces.map((space) => (
                                              <DropdownMenuItem
                                                key={space.id}
                                                onClick={() => navigate(`/space/${space.id}/build-quote?course=${encodeURIComponent(course.name)}&from=${encodeURIComponent(window.location.pathname)}`)}
                                              >
                                                <Building2 className="h-4 w-4 mr-2" />
                                                {space.name}
                                              </DropdownMenuItem>
                                            ))
                                          )}
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                      {trainer.company_id && (
                                        <>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0"
                                            onClick={() => setEditingCourse(course)}
                                          >
                                            <Edit className="h-3.5 w-3.5" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0"
                                            onClick={() => handleDeleteCourse(course.id)}
                                          >
                                            <Trash className="h-3.5 w-3.5" />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </>
                              )}
                            </motion.tr>
                          ))}

                          {/* New Course Row */}
                          {isAddingCourse && newCourse && (
                            <motion.tr
                              key="new-course"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 52 }}
                              exit={{ opacity: 0, height: 0 }}
                              className="border-b"
                            >
                              <td className="py-2 px-3">
                                <span className="font-mono text-sm text-muted-foreground">New</span>
                              </td>
                              <td className="py-2 px-3">
                                <Input
                                  value={newCourse.name}
                                  onChange={(e) => updateNewCourse('name', e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleSaveCourse(newCourse);
                                    } else if (e.key === 'Escape') {
                                      e.preventDefault();
                                      handleCancelEdit();
                                    }
                                  }}
                                  className="h-8"
                                  placeholder="Course name"
                                  autoFocus
                                />
                              </td>
                              <td className="py-2 px-3">
                                <Input
                                  value={newCourse.accreditation}
                                  onChange={(e) => updateNewCourse('accreditation', e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleSaveCourse(newCourse);
                                    } else if (e.key === 'Escape') {
                                      e.preventDefault();
                                      handleCancelEdit();
                                    }
                                  }}
                                  className="h-8"
                                  placeholder="Accreditation"
                                />
                              </td>
                              <td className="py-2 px-3">
                                <span className="text-sm">{newCourse.updatedDate}</span>
                              </td>
                              <td className="py-2 px-3">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0"
                                    onClick={() => handleSaveCourse(newCourse)}
                                  >
                                    <Check className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0"
                                    onClick={handleCancelEdit}
                                  >
                                    <X className="h-4 w-4 text-red-600" />
                                  </Button>
                                </div>
                              </td>
                            </motion.tr>
                          )}

                          {/* Add Course Button Row - Only show for company trainers */}
                          {trainer.company_id && (
                            <motion.tr
                              key="add-course"
                              layout
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="h-[52px]"
                            >
                              <td colSpan="5" className="py-2 px-3">
                                <AnimatePresence mode="wait">
                                  {!isAddingCourse && (
                                    <motion.div
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      exit={{ opacity: 0 }}
                                    >
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleAddCourse}
                                        className="w-full justify-start text-muted-foreground hover:text-foreground"
                                      >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add New Course
                                      </Button>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </td>
                            </motion.tr>
                          )}
                          </AnimatePresence>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls - Only show for BOHSA trainer */}
                  {trainerId === BOHSA_TRAINER_ID && !loadingCourses && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>
                          {pagination.totalItems > 0 ? (
                            <>
                              {(pagination.page - 1) * pagination.itemsPerPage + 1} -{' '}
                              {Math.min(pagination.page * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                              {pagination.totalItems}
                            </>
                          ) : (
                            '0 courses'
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={handlePreviousPage}
                          disabled={pagination.page <= 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-muted-foreground px-2">
                          Page {pagination.page} of {pagination.totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={handleNextPage}
                          disabled={pagination.page >= pagination.totalPages}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                    </CardContent>
                  </Card>
                  </motion.div>
                )}
                </AnimatePresence>
                  </div>
                </div>
              </Tabs>
            </div>
          </PageTransition>
        </div>
      </div>
    </AppShell3>
  );
}